import { styled } from '@mui/material/styles';
import { IFilterField } from 'models/types';
import { US_STATES } from 'projectConfig';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
// import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { get } from 'lodash';
// import clsx from 'clsx';
import GoogleMapComponent from './GoogleMapComponent';
import { useTranslation } from 'react-i18next';

const PREFIX = 'Logo';

const classes = {
  root: `${PREFIX}-root`,
};

const Root = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: '#f1f1f1',
  backgroundColor: '#f5f5f5',
  [`&.${classes.root}`]: {},
}));

interface IFilters {
  addressRegion?: string;
  streetAddressLine1?: string;
  addressLocality?: string;
  postalCode?: string;
  radiusLat?: number;
  radiusLon?: number;
  radiusPostalCode?: string;
  radiusDistance?: number;
  radiusUnit?: string;
}

interface IProps {
  loading?: boolean;
  countryCodeAlpha3?: string;
  companyName?: string;
  filters: IFilters;
  tabName: string;
  onTabChange: (name: 'address' | 'geo') => void;
  onChangeFilter: (filters: Partial<IFilters>) => void;
}

function AdvancedCompanySearchWidget(props: IProps): JSX.Element {
  const { countryCodeAlpha3, filters, onChangeFilter, tabName, onTabChange, loading } = props;

  const { t } = useTranslation('default');

  const addressLocalityFiltersSchema = [
    {
      label: t('us-states'),
      name: 'addressRegion',
      widget: 'select',
      options: US_STATES,
    },
  ];

  const addressTextFiltersSchema = [
    {
      label: t('street-address'),
      name: 'streetAddressLine1',
      widget: 'text',
    },
    {
      label: t('city-or-town'),
      name: 'addressLocality',
      widget: 'text',
    },
    {
      label: t('postal-code'),
      name: 'postalCode',
      widget: 'text',
    },
  ];

  const disabled = loading;

  const handleChange = (value: string | number, name: string) => {
    onChangeFilter({ [name]: value });
  };

  const renderField = (field: IFilterField, index: number) => {
    const { label, name, widget, options, defaultValue } = field;
    const value = get(filters, name) || defaultValue || '';

    switch (widget) {
      case 'text': {
        return (
          <div key={index}>
            <TextField
              size="small"
              value={value}
              label={label}
              name={name}
              disabled={disabled}
              onChange={(event) => handleChange(event.target.value, name)}
              sx={{ minWidth: 200 }}
            />
          </div>
        );
      }
      case 'number': {
        const max = field?.config?.max;
        const min = field?.config?.min;
        return (
          <div key={index}>
            <TextField
              size="small"
              type="number"
              inputProps={{
                min,
                max,
              }}
              value={value}
              label={label}
              name={name}
              disabled={disabled}
              onChange={(event) => handleChange(event.target.value, name)}
              sx={{ minWidth: 200 }}
            />
          </div>
        );
      }
      case 'select': {
        return (
          <div key={index}>
            <FormControl>
              <InputLabel size="small" id="demo-simple-select-label">
                {label}
              </InputLabel>
              <Select
                disabled={disabled}
                size="small"
                name={name}
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={value}
                label={label}
                onChange={(event) => handleChange(event.target.value, name)}
                sx={{ minWidth: 200 }}
              >
                {(options || []).map((op) => (
                  <MenuItem key={op.alias} value={op.alias}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        );
      }
      case 'map': {
        return (
          <div key={index}>
            <FormControl>
              <GoogleMapComponent disabled={disabled} onChangePosition={handleChangePosition} />
            </FormControl>
          </div>
        );
      }
    }
  };

  const renderFilterByAddress = () => {
    return (
      <Stack spacing={2} divider={<Divider />}>
        {countryCodeAlpha3 === 'USA' ? (
          <Stack sx={{ flexWrap: 'wrap', gap: '15px' }}>
            {addressLocalityFiltersSchema.map(renderField)}
          </Stack>
        ) : null}
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '15px' }}>
          {addressTextFiltersSchema.map(renderField)}
        </Stack>
      </Stack>
    );
  };

  const renderFilterByGeolocation = () => {
    const radiusDistanceSchema = [
      {
        label: t('radius-distance'),
        name: 'radiusDistance',
        defaultValue: 10,
        widget: 'text',
      },
      {
        label: t('radius-unit'),
        name: 'radiusUnit',
        widget: 'select',
        defaultValue: 'km',
        options: [
          { alias: 'm', label: 'meters' },
          { alias: 'km', label: 'kilometers' },
          { alias: 'mi', label: 'miles' },
          { alias: 'yd', label: 'yards' },
          { alias: 'ft', label: 'feet' },
        ],
      },
    ];

    const addressGeoFiltersSchema = [
      {
        label: t('geo'),
        name: 'geolocation',
        widget: 'map',
      },
    ];

    return (
      <Stack spacing={2} divider={<Divider />}>
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '15px' }}>
          {radiusDistanceSchema.map(renderField)}
        </Stack>
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '15px' }}>
          {addressGeoFiltersSchema.map(renderField)}
        </Stack>
      </Stack>
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    if (newValue === 'address') {
      onTabChange('address');
    } else {
      onTabChange('geo');
    }
  };

  const handleChangePosition = (lat: number, lng: number) => {
    onChangeFilter({
      radiusLat: lat,
      radiusLon: lng,
    });
  };

  return (
    <Root>
      <Box sx={{ width: '100%', typography: 'body1' }}>
        <TabContext value={tabName}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={handleTabChange} aria-label="lab API tabs example">
              <Tab label={t('search-by-address')} value="address" />
              <Tab label={t('search-from-map')} value="geo" />
            </TabList>
          </Box>
          <TabPanel value="address">{renderFilterByAddress()}</TabPanel>
          <TabPanel value="geo">{renderFilterByGeolocation()}</TabPanel>
        </TabContext>
      </Box>
    </Root>
  );
}

export default AdvancedCompanySearchWidget;
